import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Input,
  Menu,
  MenuList,
  MenuItemOption,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  VStack,
  HStack,
  Checkbox,
  Select,
  Textarea,
  Flex,
} from '@chakra-ui/react';
import useApi from 'hooks/useApi';
import { USERS_LISTING_API } from 'constants/endpoints';
import { SHARE_PLANS_API } from 'constants/endpoints';
import { showToast } from 'components/toast/Toast';
import { whatsappMessageFormat, shareToWhatsApp } from 'utils/shareToWhatsApp';
import { whatsaappMessageTypes } from 'utils/shareToWhatsApp';
import { planUserPurpose } from 'utils/shareToWhatsApp';
import { CAMPAIGN_COST } from 'constants/defaultState';

const planUserTypes={
  tenant: 'Tenant',
  buyer: 'Buyer',
  agent: 'Agent',
  pg_owner: 'PG Owner',
  landlord: 'Landlord',
  seller:  'Seller',
  co_living: 'Co Living',
  pg_seeker: 'PG Seeker'
}
const planTypes={
  pro: 'Pro',
  pro_plus: 'Pro Plus'
}


const SharePlanComponent = ({ isOpen, onClose, planData, user_id }) => {
  const { _id: planId, user_type: planUserType, type: planType } = planData || {}
  const userInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [sendSMS, setSendSMS] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendWhatsapp, setSendWhatsapp] = useState(false);
  const [messageType,setMessageType]=useState('plans');
  const [whatsappMessage,setWhatsappMessage]=useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { apiCall } = useApi();
  const menuRef = useRef();

  const fetchUsers = useCallback(
    async (query = '') => {
      try {
        const response = await apiCall.get(`${USERS_LISTING_API}?search_text=${query}`);
        const rawData = response?.data?.users || [];
        setUsers(rawData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    },
    [apiCall]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if(planData?.user_id?._id){
      setSelectedUserIds([planData?.user_id?._id]);
      setUsers([planData?.user_id])
    }
  }, [planData]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      fetchUsers(searchQuery);
    }
  };

  const handleDropdownChange = (selectedUserId) => {
    setSelectedUserIds((prevSelectedUserIds) => {
      if (prevSelectedUserIds.includes(selectedUserId)) {
        return prevSelectedUserIds.filter((id) => id !== selectedUserId);
      } else {
        return [...prevSelectedUserIds, selectedUserId];
      }
    });
  };

const handleSubmit = async () => {
  try {
    const planIdsArray = Array.isArray(planId) ? planId : [planId];
    const response = await apiCall.post(SHARE_PLANS_API, {
      plan_ids: planIdsArray,
      user_ids: selectedUserIds,
      send_sms: sendSMS,
      send_email: sendEmail
    });

    // Check if the statusCode is 200 OK
    if (response.data.statusCode === 200) {
      setSelectedUserIds([]); // Corrected function call
      showToast({
        message: response.data.message || 'Plans shared successfully',
        success: true,
        customStyle: {
          background: '#52c41a',
          color: '#fff',
          padding: '10px',
        },
      });
    } else {
      throw new Error(`Unexpected response status: ${response.data.statusCode}`);
    }
    if(sendWhatsapp){
      const user = users?.[0];
      shareToWhatsApp(whatsappMessage, user?.phone)
    }
    onClose();
  } catch (error) {
    console.error('Error sharing plans:', error);
    const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || error.message;
    showToast({
      message: `Error: ${errorMessage}`,
      success: false,
      customStyle: {
        background: '#F24052',
        color: '#fff',
        padding: '10px',
      },
    });
  }
};


  const getCurrentMessage = useCallback(
    (msgType) => {
      const template = whatsappMessageFormat[msgType || messageType];
      const user = users?.[0];
      if (!template || !user) return;

      let link = `${process.env.REACT_APP_HEXAHOME_BASE_URL}/plans/${planUserType}/?tab=${planType}`;
      if(planData?.is_custom) link = `${process.env.REACT_APP_HEXAHOME_BASE_URL}/custom-plan/?plan_id=${planData?._id}`;

      const replacements = {
        '{{user}}': user.first_name ?? '',
        '{{plan_name}}': `${planUserTypes[planUserType] ?? ''} ${planTypes[planType] ?? ''} Plan`,
        '{{user_purpose}}':planUserPurpose[planUserType],
        '{{link}}': link,
      };

      const messageFormatted = Object.entries(replacements).reduce(
        (msg, [placeholder, value]) => msg.replace(placeholder, value),
        template
      );
      setWhatsappMessage(messageFormatted);
    },
    [messageType, users, planUserType, planType, planData]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='2xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack justify="space-between" align="center">
            <Text fontSize="lg" fontWeight="bold">
              Share Plan
            </Text>
            <HStack spacing={8}>
              <Checkbox
                isChecked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              >
                Send Email
              </Checkbox>
              <Checkbox
                isChecked={sendSMS}
                onChange={(e) => setSendSMS(e.target.checked)}
              >
                Send SMS
              </Checkbox>
              <Checkbox
                isChecked={sendWhatsapp}
                onChange={(e) => {
                  if(!!users.length){
                    setSendWhatsapp(e.target.checked);
                    getCurrentMessage()
                  }else{
                    userInputRef.current.focus()
                  }
                }}
              >
               Whatsapp
              </Checkbox>
              <ModalCloseButton position="relative" top="0" />
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Box mb={4} position="relative" ref={menuRef}>
            <Input
              ref={userInputRef}
              placeholder="Search for a user..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                getCurrentMessage();
              }}
              onFocus={() => setIsMenuOpen(true)}
              onKeyDown={handleKeyDown}
              disabled={planData?.is_custom}
            />
            {isMenuOpen && (
              <Box
                position="absolute"
                width="100%"
                zIndex={1000}
                bg="white"
                boxShadow="md"
              >
                <Menu isOpen={isMenuOpen}>
                  <MenuList maxHeight="300px" overflowY="auto">
                    {users.map((user) => (
                      <MenuItemOption
                        key={user._id}
                        value={user._id}
                        onClick={() => handleDropdownChange(user._id)}
                        isChecked={selectedUserIds.includes(user._id)}
                      >
                        {user.full_name +
                          " + " +
                          user.role +
                          "   (" +
                          user.phone +
                          ")"}
                      </MenuItemOption>
                    ))}
                  </MenuList>
                </Menu>
              </Box>
            )}
          </Box>
          <VStack align="stretch" spacing={2} mt={4}>
            {selectedUserIds.map((userId) => {
              const user = users.find((u) => u._id === userId);
              return (
                <HStack key={userId} justifyContent="space-between">
                  <Text>
                    {user && `${user.full_name} (${user.phone})`}
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="red"
                    onClick={() => handleDropdownChange(userId)}
                  >
                    remove
                  </Button>
                </HStack>
              );
            })}
          </VStack>

          {sendWhatsapp && (
            <Flex gap={2} direction={'column'}>
              <Select
                value={messageType}
                onChange={(e) => {
                  setMessageType(e.target.value)
                  getCurrentMessage(e.target.value)
                }}
                size={'md'}
              >
                {whatsaappMessageTypes.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>

              <Textarea value={whatsappMessage} onChange={(e)=> setWhatsappMessage(e.target.value)} minHeight={250} />
            </Flex>
          )}
        </ModalBody>
        <ModalFooter>
          <Text mr={3} fontWeight={'bold'}>cost: {CAMPAIGN_COST.plan * selectedUserIds?.length}</Text>
          <Button
            background={"linear-gradient(180deg, #22ACEE 0%, #0177B4 100%)"}
            color="#ffffff"
            mr={3}
            onClick={handleSubmit}
            disabled={selectedUserIds.length === 0}
          >
            Submit
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SharePlanComponent;
